import React, { useEffect } from 'react';
import cn from 'classnames';
import Icon from 'components/UI/Icon';
import Loader from 'components/UI/Loader';
import Link from 'components/UI/Link';
import { useVideoControls } from './hooks';
import { useTrans } from 'utils/localisation';
import { formatTime } from './utils';
import { observer, expertTalks as store } from 'store';

import * as styles from './FullScreenPlayerCard.module.scss';

function FullScreenPlayerCard({
  fullscreen,
  title,
  expert,
  expertPosition,
  cover,
  fullVideoLink,
  active,
  handleNext,
  setGlobalPlayback,
  video,
  loadVideo,
}) {
  const t = useTrans(['insights']);
  const {
    videoRef,
    progressRef,
    onLoadedMetadata,
    togglePlayback,
    playVideo,
    resetVideo,
    playing,
    muted,
    setMuted,
    toggleMute,
    controlVolume,
    seekTo,
    loading,
    currentTime,
    progress,
    setProgress,
    duration,
    showControls,
    showAnimatedButton,
    volumeOfVideo,
    setVolumeOfVideo,
    handleDown,
    handleMove,
    handleInteractionEnd,
  } = useVideoControls(handleNext, setGlobalPlayback);

  useEffect(() => {
    if (!active) {
      resetVideo();
    }
  }, [active]);

  useEffect(() => {
    if (active && fullscreen) {
      videoRef.current.currentTime = store.currentTime;
      setProgress(store.progress);

      const volumeValue = parseFloat(store.volumeOfVideo) / 100;
      videoRef.current.volume = volumeValue.toFixed(1);

      if (store.muted) {
        setVolumeOfVideo(0);
      } else {
        setVolumeOfVideo(store.volumeOfVideo);
      }
      videoRef.current.muted = store.muted;
      setMuted(store.muted);

      if (store.playing) {
        playVideo();
      }
    }
  }, [store.currentIndex, fullscreen]);

  const textContent = (
    <>
      <h4 className={styles.title}>{title}</h4>
      <div className={styles.text}>
        <p className={styles.expertWrapper}>
          <span className={styles.name}>{expert}</span>
          <span className={styles.position}>{expertPosition}</span>
        </p>
        {fullVideoLink && (
          <Link className={styles.videoLink} href={fullVideoLink}>
            {t('watchFullInterview')}
          </Link>
        )}
      </div>
    </>
  );

  return (
    <article
      className={cn(styles.card, !active && styles.notActiveSlide)}
      onClick={active ? undefined : handleNext}
    >
      <div className={cn(styles.textWrapper, styles.showOnDesktop)}>{textContent}</div>
      <div
        className={cn(
          styles.videoWrapper,
          showControls && styles.showControls,
          playing && styles.hideWhilePlaying,
        )}
      >
        {loading && <Loader theme="fullHeight" />}
        <video
          preload="none"
          className={styles.video}
          src={loadVideo ? video : ''}
          ref={videoRef}
          poster={cover}
          playsInline={true}
          controls={false}
          autoPlay={false}
          onClick={togglePlayback}
          onLoadedMetadata={onLoadedMetadata}
          onPlay={e => {
            const videoElem = e.target;
            const slideElem = videoElem.closest('.slick-slide');
            if (slideElem && !slideElem.classList.contains('slick-active')) {
              videoElem.muted = true;
              videoElem.play().then(() => videoElem.pause());
            }
          }}
        />
        {!playing && showAnimatedButton && (
          <button className={cn(styles.playerFloatingButton, styles.showOnDesktop)}>
            <Icon name="pause" className={styles.playerButtonIcon} />
          </button>
        )}
        {playing && showAnimatedButton && (
          <button className={cn(styles.playerFloatingButton, styles.showOnDesktop)}>
            <Icon name="play-triangle" className={styles.playerButtonIcon} />
          </button>
        )}
        {!store.playing && (
          <button className={styles.playerFloatingButton} onClick={togglePlayback}>
            <Icon name="play-triangle" className={styles.playerButtonIcon} />
          </button>
        )}
        <div className={styles.content}>
          <div className={cn(styles.textWrapper, styles.showOnTablet)}>{textContent}</div>
          <div className={styles.videoControls}>
            <div className={styles.leftSide}>
              <button className={styles.playerButton} onClick={togglePlayback}>
                {playing ? (
                  <Icon name="pause" className={styles.playerButtonIcon} />
                ) : (
                  <Icon name="play-triangle" className={styles.playerButtonIcon} />
                )}
              </button>
              <div className={styles.volume}>
                {muted ? (
                  <Icon name="volume-off" className={styles.volumeIcon} onClick={toggleMute} />
                ) : (
                  <Icon name="volume" className={styles.volumeIcon} onClick={toggleMute} />
                )}
                <div
                  className={styles.volumeInputContainer}
                  style={{ '--volumePercentage': `${volumeOfVideo}%` }}
                >
                  <input
                    className={styles.volumeInput}
                    type="range"
                    value={volumeOfVideo}
                    onChange={controlVolume}
                  />
                </div>
              </div>
              <div className={styles.time}>
                {`${formatTime(currentTime)} / ${formatTime(duration)}`}
              </div>
            </div>
          </div>
        </div>
        <div
          className={styles.progressBarContainer}
          ref={progressRef}
          role="progressbar"
          style={{ '--progress': progress, '--marginLeft': `${progress * 100}%` }}
          onClick={seekTo}
          onMouseDown={handleDown}
          onMouseUp={handleInteractionEnd}
          onMouseMove={handleMove}
          onTouchMove={handleMove}
          onTouchStart={handleDown}
          onTouchEnd={handleInteractionEnd}
          onMouseLeave={handleInteractionEnd}
        >
          <div className={styles.progressBarBackground}></div>
          <div className={styles.progressBar}></div>
          <div className={styles.playerHead}></div>
        </div>
      </div>
    </article>
  );
}

export default observer(FullScreenPlayerCard);
